import { useEffect, useMemo, useState } from 'react';

import { County, getCounties } from '@/endpoints/itasca/county/getCounties';
import { AcresFeature } from '@/models/features';
import { isParcelFeature, isSaveFeature } from '@/models/features/guards';
import centroid from '@/utils/centroid';

type Data = {
  feature: AcresFeature;
  shouldFetch: boolean;
};

export const useGetLocationInfo = ({ feature, shouldFetch = true }: Data) => {
  const [data, setData] = useState<County | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const center = useMemo(() => centroid(feature.geometry), [feature.geometry]);

  useEffect(() => {
    if (
      (isParcelFeature(feature) || isSaveFeature(feature)) &&
      feature.properties?.state &&
      feature.properties.county_name
    ) {
      return;
    }
    if (!center || !shouldFetch) return;

    const [lon, lat] = center.geometry.coordinates;

    getCounties({ location: { lat: lat ?? 0, lon: lon ?? 0 } })
      .then(({ res }) => {
        const county = res?.data.results?.[0];
        if (county) {
          setData(county);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [shouldFetch]);

  const state = useMemo(() => {
    if (
      (isParcelFeature(feature) || isSaveFeature(feature)) &&
      feature.properties?.state
    )
      return feature.properties?.state;
    return data?.state_abbrev ?? undefined;
  }, [data?.state_abbrev, feature]);

  const county = useMemo(() => {
    if (
      (isParcelFeature(feature) || isSaveFeature(feature)) &&
      feature.properties.county_name
    )
      return feature.properties.county_name;
    return data?.name ?? undefined;
  }, [data?.name, feature]);

  const locationText = useMemo(() => {
    if (!state || !county) return '–';

    if (county && state) {
      return `${county}, ${state}`;
    }

    return '–';
  }, [state, county]);

  return { county, isLoading, state, text: locationText };
};
